import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuth, signOut } from 'firebase/auth';
import accountService from '../services/accountService';
import NavBar from '../components/NavBar';
import Footer from '../components/Footer';
import AvatarInitials from '../components/AvatarInitials';
import '../styles/AccountPage.css';

const AccountPage = () => {
  const navigate = useNavigate();
  const auth = getAuth();
  const user = auth.currentUser;
  
  const [activeTab, setActiveTab] = useState('profile');
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  // eslint-disable-next-line no-unused-vars
  const [error, setError] = useState(null);
  const [profileData, setProfileData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    city: '',
    province: '',
    birthdate: '',
    favoriteGenre: '',
    avatar: null
  });
  // eslint-disable-next-line no-unused-vars
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);

  useEffect(() => {
    if (!user) {
      navigate('/login');
    } else {
      loadUserData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, navigate]);

  const loadUserData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load user profile
      const profile = await accountService.getUserProfile(user.uid);
      setProfileData(profile);

      // Load subscription
      const subscription = await accountService.getUserSubscription(user.uid);
      setSubscriptionData(subscription);

      // Check subscription status
      const status = await accountService.checkSubscriptionStatus(user.uid);
      setSubscriptionStatus(status);

    } catch (err) {
      setError('Failed to load user data. Please try again.');
      console.error('Error loading user data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      // Update profile
      await accountService.updateUserProfile(user.uid, profileData);
      
      // Update subscription with the same data
      await accountService.updateSubscription(user.uid, {
        plan: subscriptionStatus?.plan || 'Free',
        duration: subscriptionStatus?.duration || 0,
        startDate: subscriptionStatus?.startDate || new Date(),
        endDate: subscriptionStatus?.endDate || new Date()
      });
      
      await loadUserData();
      setIsEditing(false);
    } catch (err) {
      setError('Failed to update profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line no-unused-vars
  const handleSubscriptionPurchase = async () => {
    try {
      setLoading(true);
      await accountService.updateSubscription(user.uid, {
        plan: 'daypass',
        duration: 1
      });
      await loadUserData();
    } catch (err) {
      setError('Failed to purchase subscription. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (window.confirm('Are you sure you want to delete your account? This action cannot be undone.')) {
      try {
        setLoading(true);
        await accountService.deleteAccount(user.uid);
        await signOut(auth);
        navigate('/');
      } catch (err) {
        setError('Failed to delete account. Please try again.');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleDayPassPurchase = () => {
    navigate('/payment');
  };

  if (loading) {
    return (
      <div className="account-page">
        <NavBar />
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="account-page">
      <NavBar />
      <div className="account-container">
        <div className="account-header">
          <h1>My Account</h1>
        </div>

        <div className="account-content">
          <div className="account-sidebar">
            <div className="user-brief">
              <div className="avatar-container">
                {profileData.avatar ? (
                  <img 
                    src={profileData.avatar} 
                    alt="Profile" 
                    className="user-avatar"
                  />
                ) : (
                  <AvatarInitials 
                    firstName={profileData.firstName}
                    lastName={profileData.lastName}
                    email={profileData.email || user?.email}
                    size={100}
                    className="user-avatar"
                  />
                )}
              </div>
              <h2>{profileData.firstName} {profileData.lastName}</h2>
              <p className="user-email">{profileData.email}</p>
            </div>
            <nav className="account-nav">
              <button 
                className={`nav-button ${activeTab === 'profile' ? 'active' : ''}`}
                onClick={() => setActiveTab('profile')}
              >
                <i className="fas fa-user"></i>
                Profile
              </button>
              <button 
                className={`nav-button ${activeTab === 'subscription' ? 'active' : ''}`}
                onClick={() => setActiveTab('subscription')}
              >
                <i className="fas fa-credit-card"></i>
                Subscription
              </button>
              <button 
                className={`nav-button ${activeTab === 'history' ? 'active' : ''}`}
                onClick={() => setActiveTab('history')}
              >
                <i className="fas fa-history"></i>
                Session History
              </button>
              <button 
                className={`nav-button ${activeTab === 'settings' ? 'active' : ''}`}
                onClick={() => setActiveTab('settings')}
              >
                <i className="fas fa-cog"></i>
                Settings
              </button>
            </nav>
          </div>

          <div className="account-main">
            {activeTab === 'profile' && (
              <div className="profile-section">
                <div className="section-header">
                  <h2>Profile Information</h2>
                  <button 
                    className="edit-button"
                    onClick={() => setIsEditing(!isEditing)}
                  >
                    {isEditing ? 'Cancel' : 'Edit Profile'}
                  </button>
                </div>

                <form onSubmit={handleProfileUpdate}>
                  <div className="form-row">
                    <div className="form-group">
                      <label>First Name</label>
                      <div className="input-wrapper">
                        <i className="fas fa-user"></i>
                        <input
                          type="text"
                          value={profileData.firstName}
                          onChange={(e) => setProfileData({...profileData, firstName: e.target.value})}
                          disabled={!isEditing}
                        />
                      </div>
                    </div>

                    <div className="form-group">
                      <label>Last Name</label>
                      <div className="input-wrapper">
                        <i className="fas fa-user"></i>
                        <input
                          type="text"
                          value={profileData.lastName}
                          onChange={(e) => setProfileData({...profileData, lastName: e.target.value})}
                          disabled={!isEditing}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Email</label>
                    <div className="input-wrapper">
                      <i className="fas fa-envelope"></i>
                      <input
                        type="email"
                        value={profileData.email}
                        onChange={(e) => setProfileData({...profileData, email: e.target.value})}
                        disabled={!isEditing}
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>City</label>
                      <div className="input-wrapper">
                        <i className="fas fa-city"></i>
                        <input
                          type="text"
                          value={profileData.city}
                          onChange={(e) => setProfileData({...profileData, city: e.target.value})}
                          disabled={!isEditing}
                        />
                      </div>
                    </div>

                    <div className="form-group">
                      <label>Province</label>
                      <div className="input-wrapper">
                        <i className="fas fa-map-marker-alt"></i>
                        <input
                          type="text"
                          value={profileData.province}
                          onChange={(e) => setProfileData({...profileData, province: e.target.value})}
                          disabled={!isEditing}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Date of Birth</label>
                      <div className="input-wrapper">
                        <i className="fas fa-calendar"></i>
                        <input
                          type="date"
                          value={profileData.birthdate}
                          onChange={(e) => setProfileData({...profileData, birthdate: e.target.value})}
                          disabled={!isEditing}
                        />
                      </div>
                    </div>

                    <div className="form-group">
                      <label>Favorite Genre</label>
                      <div className="input-wrapper">
                        <i className="fas fa-music"></i>
                        <input
                          type="text"
                          value={profileData.favoriteGenre}
                          onChange={(e) => setProfileData({...profileData, favoriteGenre: e.target.value})}
                          disabled={!isEditing}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Phone Number</label>
                    <div className="input-wrapper">
                      <i className="fas fa-phone"></i>
                      <input
                        type="tel"
                        value={profileData.phone}
                        onChange={(e) => setProfileData({...profileData, phone: e.target.value})}
                        disabled={!isEditing}
                        placeholder="09xxxxxxxxx"
                      />
                    </div>
                  </div>

                  {isEditing && (
                    <button type="submit" className="save-button">
                      Save Changes
                    </button>
                  )}
                </form>
              </div>
            )}

            {activeTab === 'subscription' && (
              <div className="subscription-section">
                <h2>Subscription Details</h2>
                <div className="subscription-card">
                  <div className="subscription-status">
                    <span className={`status-badge ${subscriptionStatus?.plan}`}>
                      {subscriptionStatus?.plan === 'daypass' ? 'Day Pass' : 'Free Plan'}
                    </span>
                  </div>
                  <p>
                    {subscriptionStatus?.isActive 
                      ? `Your Day Pass is active until ${new Date(subscriptionStatus.endDate.seconds * 1000).toLocaleString()}`
                      : 'You are currently on the Free Plan'}
                  </p>
                  {!subscriptionStatus?.isActive && (
                    <button 
                      className="upgrade-button"
                      onClick={handleDayPassPurchase}
                    >
                      Get Day Pass (₱299)
                    </button>
                  )}
                </div>
                
                <h3>Purchase History</h3>
                <div className="purchase-history">
                  <p className="no-history">No recent purchases</p>
                </div>
              </div>
            )}

            {activeTab === 'history' && (
              <div className="history-section">
                <h2>Session History</h2>
                <div className="session-list">
                  <div className="session-item">
                    <div className="session-info">
                      <h3>Karaoke Night</h3>
                      <p>March 15, 2024 • 2 hours</p>
                    </div>
                    <button className="view-button">View Details</button>
                  </div>
                  {/* Add more session items here */}
                </div>
              </div>
            )}

            {activeTab === 'settings' && (
              <div className="settings-section">
                <h2>Account Settings</h2>
                
                <div className="settings-group">
                  <h3>Notifications</h3>
                  <div className="setting-item">
                    <label className="toggle-switch">
                      <input type="checkbox" />
                      <span className="toggle-slider"></span>
                    </label>
                    <div className="setting-info">
                      <h4>Email Notifications</h4>
                      <p>Receive updates about your sessions and account</p>
                    </div>
                  </div>
                </div>

                <div className="settings-group">
                  <h3>Privacy</h3>
                  <div className="setting-item">
                    <label className="toggle-switch">
                      <input type="checkbox" />
                      <span className="toggle-slider"></span>
                    </label>
                    <div className="setting-info">
                      <h4>Public Profile</h4>
                      <p>Allow others to see your profile</p>
                    </div>
                  </div>
                </div>

                <div className="danger-zone">
                  <h3>Danger Zone</h3>
                  <button className="delete-account-button" onClick={handleDeleteAccount}>
                    Delete Account
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default AccountPage; 